"use client";
/**
 * The dock composition (spec 2026-09-12 §2 to §7; mock of record
 * mockups/composer-ball-life-2026-09-12.html, the founder's pick). The ball
 * sits in a seat, a shallow dish of light; beneath it one fixed row holds
 * exactly one thing at a time, crossfaded in place so the ball never moves:
 * the door ("or type") at rest, the whisper ("Click to talk · or type") while
 * the pointer or focus is on the ball, the one word "waking" while the line
 * connects, a note from Bash (the closed sentence, an error with its fix,
 * "Still there?", the silent-mic line while no input has signal), or the tray
 * (the timer, type, end) while the line is open.
 * The ready beat is a 1.4 s timer from the started transition: the crest, the
 * chime (useLine), the eyes opening onto you, no word, then the tray. The
 * gaze is two CSS variables written on the stage from a pointer listener,
 * one animation frame per move, never React state and never a frame loop.
 * The work of a spoken turn renders inside the ball's box (LineWork), so the
 * receipt surfaces from the crown; the stage passes the glyph, the spark key
 * and the nod down to the ball and keeps the 1.7 s check after a clean sink.
 * On a line that takes files (attach on the call), the card of them rides
 * between the ball and the row (LineFiles): the one thing that lifts the ball.
 * Every rule lives in globals.css (the cl- stage section after the ball's).
 *
 * Every beat here begins on the render that brings its cause (state adjusted
 * during render, React's pattern for information from previous renders, the
 * adjusted value carried through the same pass) and only a timer ends it:
 * nothing is set from an effect body, so no row item ever mounts for one
 * paint before the beat that should hide it.
 */
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import Link from "next/link";
import type { RouterEvent } from "@bash/shared";
import { cn } from "@/lib/utils";
import { forwardRoute } from "@/lib/dock-window";
import type { FaceMood } from "@/components/bash-home/BashFace";
import { Ball } from "./Ball";
import { faceMoodFor, toneFor, type LineSnapshot } from "./lineMachine";
import { KeyboardGlyph, LineTray } from "./LineTray";
import { LineWork, useWork } from "./LineWork";
import { LineFiles } from "./LineFiles";
import type { LineAttach } from "./useLineAttach";

export interface BallStageProps {
  line: LineSnapshot;
  /** The relayed events of the current spoken turn (the store's turn), or
   *  [] while another card owns the slot (a typed card streaming, history). */
  turn: RouterEvent[];
  onToggle: () => void;
  onType: () => void;
  /** The paper left the crown: the Dock's resetTurn. */
  onDismissReceipt: () => void;
  /** Settle the mic permission prompt off the click path (the Dock's
   *  primeOnHover, latched there). It hangs on the BALL's own hover and focus,
   *  not the stage's: the stage wraps "or type" and the whisper row too, and
   *  React's onFocus rides native focusin, which bubbles — so a click on "or
   *  type", which means "not voice", was asking for the microphone. */
  onPrime?: () => void;
  /** The mic is wired (the Dock's live level): the tray gets its mute. */
  onMute?: (muted: boolean) => void;
  muted?: boolean;
  /** The mic-trouble level (the store's micTrouble): the row says so, in one sentence. */
  micTrouble?: null | "silent" | "deaf";
  size?: number;
  energyRef?: { current: number };
  bashRef?: { current: number };
  talkRef?: { current: 0 | 1 | 2 | 3 };
  /** Dev page only: park the stage in a moment the clock would move past. */
  hold?: "hover" | "ready" | "check";
  /** The shell's Dock window (contract 2026-09-23): a moment for the crown,
   *  drawn only while the Line's own work is not on it. */
  crown?: ReactNode;
  /** The shell's Dock window: what sits beside the ball (the task dots). */
  aside?: ReactNode;
  /** The shell's Dock window: the Mac's one line, in the row at rest in
   *  place of the door. */
  hostNote?: string | null;
  /** Attach on the call (the Dock's useLineAttach): the tray's attach seat
   *  while the line takes files, the card of files between the ball and the
   *  row, and a refusal as the row's note. */
  attach?: LineAttach;
  /** A file is dragged over the stage (the Dock's drop): on a line that
   *  takes files, a ring round the ball and "Drop files to attach". */
  dragOver?: boolean;
}

/** The notes (spec §7): a sentence from Bash with the fix in it, keyed on
 *  what the client throws by name (liveClient.ts, useLine.ts). */
const NOTES: Record<string, string> = {
  no_mic:
    "I cannot hear a microphone. Check it is plugged in, then click me again.",
  mic_failed:
    "I cannot hear a microphone. Check it is plugged in, then click me again.",
  mic_denied:
    "I need the microphone. Allow it in the address bar, then click me again.",
  live_disabled: "Voice is not on here yet. You can type to me instead.",
  live_unconfigured: "Voice is not on here yet. You can type to me instead.",
  credits_exhausted: "You are out of credits. Top up and I am here.",
  not_signed_in: "Sign in and I am here.",
};
const NOTE_FALLBACK = "I could not open the line. Click me again.";
const STILL_THERE = "Still there?";
/** The silent mic (2026-09-21): no input has signal. Like the credits note,
 *  the sentence's own words are its door: the one string, split at these,
 *  with the type door in their place. */
const MIC_SILENT =
  "I can't hear your microphone. Check your sound input, or type instead.";
/** The deaf call (2026-09-21, R-D1): the mic was fine and the line was redialled anyway, and the
 *  second one could not hear him either. Said once, and only after the one silent redial. */
const MIC_DEAF =
  "I still can't hear you. Try your other microphone, or type instead.";
const TYPE_INSTEAD = "type instead";
/** The row while a file is dragged over the ball on a line that takes files. */
export const DROP_WORDS = "Drop files to attach";
/** The credits note's door is the sentence's own words: the one string,
 *  split at these, with the link in their place. */
const TOP_UP = "Top up";

/** The closed note holds to 72% of its four seconds (clNote); an error stays
 *  twice as long so a sentence can be read twice. */
export const NOTE_MS = 4000;
export const ERROR_MS = 8000;
/** The ready beat (spec §4): the crest, the chime, the eyes opening, no word; then the tray. */
export const READY_BEAT_MS = 1400;
/** The check in the glass after a clean receipt sinks (spec §6). */
export const CHECK_MS = 1700;
/** The tray sinks on the .6 s spring; it stays mounted that long after it goes down. */
export const TRAY_SINK_MS = 600;
/** A second click on the ball inside this window after the first is the
 *  double click (the natural first gesture), not a change of mind. */
const DOUBLE_CLICK_MS = 400;
/** The gaze (spec §2): the pointer against the ball's centre, divided by
 *  these and clamped to plus or minus 1. */
const GAZE_X_PX = 360;
const GAZE_Y_PX = 280;

const clamp1 = (n: number) => Math.max(-1, Math.min(1, n));

/** The person asked for less motion: the gaze writes nothing at all. Read at
 *  the moment it matters (an effect), never during render. */
function reducedMotion(): boolean {
  return (
    typeof window.matchMedia === "function" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );
}

/** A note's words for the row; the credits sentence carries its door. */
function noteBody(note: string, withDoor: boolean) {
  const at = withDoor ? note.indexOf(TOP_UP) : -1;
  if (at < 0) return note;
  return (
    <>
      {note.slice(0, at)}
      <Link
        href="/account/plan"
        className="cl-stage__note-link"
        // The shell's Dock window: the plan opens in the main window, never the panel.
        onClick={(e) => {
          if (forwardRoute("/account/plan")) e.preventDefault();
        }}
      >
        {TOP_UP}
      </Link>
      {note.slice(at + TOP_UP.length)}
    </>
  );
}

export function BallStage({
  line,
  turn,
  onToggle,
  onType,
  onPrime,
  onDismissReceipt,
  onMute,
  muted = false,
  micTrouble = null,
  size = 128,
  energyRef,
  bashRef,
  talkRef,
  hold,
  crown,
  aside,
  hostNote = null,
  attach,
  dragOver = false,
}: BallStageProps) {
  const stageRef = useRef<HTMLDivElement>(null);
  const off = line.phase === "off";
  const openish = line.phase === "open" || line.phase === "closing";

  // Hover exists only where hover exists: a fine pointer, or keyboard focus.
  const [hovering, setHovering] = useState(false);
  const hover = off && (hovering || hold === "hover");

  // The ready beat: 1.4 s from the started transition, the phase turning open
  // under a mounted stage. A stage that mounts on a line already open (the
  // type door closing mid-line) saw no transition and has no beat: the tray
  // is simply up. Each transition takes its own serial, so the timer always
  // ends the beat it started.
  const [prevPhase, setPrevPhase] = useState(line.phase);
  const [beat, setBeat] = useState(0);
  let beatNow = beat;
  if (prevPhase !== line.phase) {
    beatNow = line.phase === "open" ? beat + 1 : 0;
    setPrevPhase(line.phase);
    setBeat(beatNow);
  }
  useEffect(() => {
    if (!beat) return;
    const t = setTimeout(() => setBeat(0), READY_BEAT_MS);
    return () => clearTimeout(t);
  }, [beat]);
  const ready = line.phase === "open" && (beatNow > 0 || hold === "ready");

  // Whether the eyes are on you (the pointer or focus on the ball at rest,
  // or the ready beat), for the gaze listener. Written by the hover
  // handlers themselves as well as after the render, so the first move
  // after a leave, landing in the same input batch as the leave, is not
  // dropped while the effect waits its turn.
  const onYou = useRef(false);
  const setHover = (on: boolean) => {
    setHovering(on);
    onYou.current = (off && (on || hold === "hover")) || ready;
  };

  // A note sits in the row for its beat, then the door returns. The machine
  // keeps closedNote/error until the next open; the beat counts from the
  // moment the machine stamped it (noteAt), so a stage mounted later (back
  // from the type door) shows only what is left of the beat, or nothing: a
  // past line's goodbye never replays. A note with no clock is fresh.
  const error = line.error ? (NOTES[line.error] ?? NOTE_FALLBACK) : null;
  const note = error ?? line.closedNote;
  const noteAt = line.noteAt;
  const noteBeat = error ? ERROR_MS : NOTE_MS;
  const noteLeft =
    noteAt === null ? noteBeat : noteBeat - (Date.now() - noteAt);
  const [prevNote, setPrevNote] = useState(note);
  const [noteOver, setNoteOver] = useState(noteLeft <= 0);
  let noteOverNow = noteOver;
  if (prevNote !== note) {
    noteOverNow = noteLeft <= 0;
    setPrevNote(note);
    setNoteOver(noteOverNow);
  }
  useEffect(() => {
    if (!note) return;
    const left = noteAt === null ? noteBeat : noteBeat - (Date.now() - noteAt);
    if (left <= 0) return;
    const t = setTimeout(() => setNoteOver(true), left);
    return () => clearTimeout(t);
  }, [note, noteAt, noteBeat]);
  const showNote = off && note !== null && !noteOverNow;
  // The silent mic is the open line's, and the row holds one thing: it
  // outranks Still there?, a question nobody could answer into a dead mic.
  const silent = line.phase === "open" && micTrouble !== null;
  // One row, one sentence: the deaf call's words outrank the watchdog's,
  // because by then the microphone has already been ruled out.
  const troubleWords = micTrouble === "deaf" ? MIC_DEAF : MIC_SILENT;
  const still = openish && line.idleAsk && !silent;
  // A file the call refused says so in the row for its beat (the door's own
  // words, or a pick the lane turned away), after the mic and Still there?.
  const files = attach?.on ? attach : null;
  const refused = line.phase === "open" && !silent && !still && !!files?.note;
  // A drop on the ball stays on the call (the Dock stages it): the pill's own
  // drop ring wraps the ball and the row says so, over the tray.
  const dropping = !!files && dragOver;

  // The check: 1.7 s after a clean receipt sinks.
  const [checking, setChecking] = useState(false);
  useEffect(() => {
    if (!checking) return;
    const t = setTimeout(() => setChecking(false), CHECK_MS);
    return () => clearTimeout(t);
  }, [checking]);
  const check = checking || hold === "check";
  const dismissReceipt = useCallback(
    (r: { clean: boolean }) => {
      onDismissReceipt();
      if (r.clean) setChecking(true);
    },
    [onDismissReceipt]
  );

  const work = useWork(turn, line, check);
  // The check yields to trouble for good: a late error of the same delegation
  // resurfacing the paper inside the check window drops the timer, so an
  // early dismiss of that trouble never brings the done mark back (the hook
  // already hides it while the trouble is up; this keeps it from returning).
  if (work.trouble !== null && checking) setChecking(false);

  // The tray belongs to the open line (the mock's tray rides
  // [data-phase="open"]): up after the beat, down while Still there? has the
  // row, and already going down at closing so the closed note finds the row
  // free. It stays mounted while it sinks.
  const trayUp =
    line.phase === "open" && !ready && !still && !silent && !refused;
  const [trayMounted, setTrayMounted] = useState(trayUp);
  if (trayUp && !trayMounted) setTrayMounted(true);
  useEffect(() => {
    if (trayUp) return;
    const t = setTimeout(() => setTrayMounted(false), TRAY_SINK_MS);
    return () => clearTimeout(t);
  }, [trayUp]);

  // The gaze: the pupils follow the pointer across the whole page (the
  // pupils' own .28 s transition is the spring). One write per move, inside
  // one animation frame, nothing between moves; zero while the pointer or
  // focus is on the ball and during the ready beat (it looks at you); off
  // under reduced motion or a touch pointer. Only at rest (the founder,
  // 2026-09-12: "the eyes following the cursor can stop when bash is active,
  // its only for inactive"): the listener is mounted while the line is off
  // and unmounts, centring the eyes, the moment it opens.
  useEffect(() => {
    const el = stageRef.current;
    if (!el || !off || reducedMotion()) return;
    let pending = false;
    let raf = 0;
    let gx = 0;
    let gy = 0;
    const write = () => {
      pending = false;
      el.style.setProperty("--gx", gx.toFixed(3));
      el.style.setProperty("--gy", gy.toFixed(3));
    };
    const schedule = () => {
      if (pending) return;
      pending = true;
      raf = window.requestAnimationFrame(write);
    };
    const onMove = (e: PointerEvent) => {
      if (e.pointerType === "touch" || onYou.current) return;
      const ball = el.querySelector<HTMLElement>(".cl-orb");
      if (!ball) return;
      const r = ball.getBoundingClientRect();
      gx = clamp1((e.clientX - (r.left + r.width / 2)) / GAZE_X_PX);
      gy = clamp1((e.clientY - (r.top + r.height / 2)) / GAZE_Y_PX);
      schedule();
    };
    const onLeave = () => {
      gx = 0;
      gy = 0;
      schedule();
    };
    window.addEventListener("pointermove", onMove, { passive: true });
    document.addEventListener("pointerleave", onLeave);
    return () => {
      window.removeEventListener("pointermove", onMove);
      document.removeEventListener("pointerleave", onLeave);
      if (pending) window.cancelAnimationFrame(raf);
      el.style.removeProperty("--gx");
      el.style.removeProperty("--gy");
    };
  }, [off]);
  useEffect(() => {
    onYou.current = hover || ready;
    if (!onYou.current || reducedMotion()) return;
    const el = stageRef.current;
    el?.style.setProperty("--gx", "0");
    el?.style.setProperty("--gy", "0");
  }, [hover, ready]);

  // The face says trouble and errors (puzzled); the machine says the rest.
  const mood: FaceMood =
    (showNote && error) || work.trouble !== null
      ? "puzzled"
      : faceMoodFor(line);
  const tone = toneFor(line);
  const label = openish ? "End the line" : "Open the line";
  const hostLine = off && !showNote && !hover ? hostNote : null;
  const door =
    (off && !showNote && !hover && !hostLine) || (ready && !still && !refused);

  const onBall = () => {
    if (
      line.phase === "connecting" &&
      line.connectingAt !== null &&
      Date.now() - line.connectingAt < DOUBLE_CLICK_MS
    )
      return;
    onToggle();
  };

  return (
    <div
      ref={stageRef}
      className="cl-stage relative flex flex-col items-center"
      data-phase={line.phase}
      data-hover={hover ? "" : undefined}
      data-ready={ready ? "" : undefined}
      data-drag={dropping ? "" : undefined}
      style={{ "--cl-size": `${size}px` } as React.CSSProperties}
    >
      <div
        className="cl-stage__ball relative"
        onPointerEnter={(e) => {
          onPrime?.();
          if (e.pointerType !== "touch") setHover(true);
        }}
        onPointerLeave={() => setHover(false)}
        onFocus={() => {
          onPrime?.();
          setHover(true);
        }}
        onBlur={() => setHover(false)}
      >
        <span className="cl-stage__seat" aria-hidden />
        <Ball
          size={size}
          mood={mood}
          tone={tone}
          glyph={work.glyph}
          glyphKey={line.stepSeq}
          sparkKey={line.births}
          nod={line.justDone}
          energyRef={energyRef}
          bashRef={bashRef}
          talkRef={talkRef}
          onClick={onBall}
          label={label}
        />
        {/* The crown's one live region is here before the paper (always
            mounted, empty at rest), so a reader hears the receipt land: a
            region that mounts with its words is never announced. */}
        <div role="status">
          <LineWork view={work} onDismiss={dismissReceipt} />
        </div>
        {!work.up && crown}
        {aside}
        {files && (
          <span
            aria-hidden
            data-ring=""
            className={cn(
              "pointer-events-none absolute -inset-3.5 z-2 rounded-full border-2 border-dashed border-gold-molten-mid-40 bg-gold-molten-hi-8 [transition:opacity_0.2s_ease,scale_0.35s_var(--cl-spring)]",
              dropping ? "scale-100 opacity-100" : "scale-[0.92] opacity-0"
            )}
          />
        )}
      </div>
      {attach && (
        <LineFiles
          rows={files ? files.rows : []}
          removable={attach.removable}
          onRemove={attach.remove}
        />
      )}
      <div className="cl-stage__under relative w-full">
        {line.phase === "connecting" && (
          <>
            <span className="sr-only">Opening the line</span>
            <span className="cl-stage__word" aria-hidden="true">
              waking
            </span>
          </>
        )}
        {showNote && note && (
          <span
            className={cn(
              "cl-stage__note",
              error ? "cl-stage__note--error" : "cl-stage__note--closed"
            )}
            role="status"
          >
            {noteBody(note, line.error === "credits_exhausted")}
          </span>
        )}
        {still && (
          <span className="cl-stage__note cl-stage__note--still" role="status">
            {STILL_THERE}
          </span>
        )}
        {silent && (
          <span className="cl-stage__note" role="status">
            {/* One item in the pill's flex row, so the sentence wraps as prose. */}
            <span className="text-balance">
              {troubleWords.slice(0, troubleWords.indexOf(TYPE_INSTEAD))}
              <button
                type="button"
                className="cl-stage__note-link cursor-pointer"
                onClick={onType}
              >
                {TYPE_INSTEAD}
              </button>
              {troubleWords.slice(
                troubleWords.indexOf(TYPE_INSTEAD) + TYPE_INSTEAD.length
              )}
            </span>
          </span>
        )}
        {refused && (
          <span className="cl-stage__note cl-stage__note--error" role="status">
            {files?.note}
          </span>
        )}
        {hostLine && (
          <span className="cl-stage__note" role="status">
            {hostLine}
          </span>
        )}
        {door && (
          <button type="button" className="cl-stage__door" onClick={onType}>
            <KeyboardGlyph size={15} />
            or type
          </button>
        )}
        {off && !showNote && hover && (
          <span className="cl-stage__whisper">
            <button type="button" className="cl-stage__talk" onClick={onToggle}>
              Click to talk
            </button>
            <span className="cl-stage__sep" aria-hidden="true">
              ·
            </span>
            <button type="button" className="cl-stage__door" onClick={onType}>
              <KeyboardGlyph size={15} />
              or type
            </button>
          </span>
        )}
        {(trayMounted || trayUp) && (
          <LineTray
            up={trayUp}
            seconds={line.seconds}
            onType={onType}
            onEnd={onToggle}
            muted={muted}
            {...(onMute ? { onMute } : {})}
            {...(files
              ? { onAttach: files.pick, attached: files.rows.length }
              : {})}
          />
        )}
        {dropping && (
          <span
            aria-hidden
            className="pointer-events-none absolute top-1.5 left-1/2 inline-flex h-7.5 -translate-x-1/2 items-center rounded-full bg-bg-elevated px-3 text-3 font-medium whitespace-nowrap text-gold-ink shadow-(--cl-tray-shade) [animation:clFadeIn_0.25s_var(--ease-smooth)_backwards]"
          >
            {DROP_WORDS}
          </span>
        )}
      </div>
    </div>
  );
}
